import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { money, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { toast } from "sonner";
import { ShieldCheck, Loader2, ArrowLeft, BadgeCheck, Ruler, Palette, MapPin, ScrollText, Percent } from "lucide-react";

export default function ArtworkDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [art, setArt] = useState(null);
  const [buying, setBuying] = useState(false);
  const [useRebate, setUseRebate] = useState(false);
  const [insurance, setInsurance] = useState(false);

  useEffect(() => {
    api.get(`/artworks/${id}`).then((r) => setArt(r.data)).catch(() => toast.error("Artwork not found"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const isDesigner = user && user.buyer_type === "designer" && user.trade_verified;
  const discountPct = isDesigner ? user.rebate_discount_pct : 0;
  const credit = isDesigner ? user.rebate_credit : 0;

  const insuranceFee = insurance ? (art?.price || 0) * 0.02 : 0;
  let itemEstimated = art?.price || 0;
  if (useRebate && isDesigner) {
    if (discountPct > 0) itemEstimated -= (itemEstimated * discountPct) / 100;
    if (credit > 0) itemEstimated -= Math.min(credit, itemEstimated);
    itemEstimated = Math.max(itemEstimated, 0);
  }
  const estimated = itemEstimated + insuranceFee;

  const buy = async () => {
    if (!user) { navigate("/login"); return; }
    setBuying(true);
    try {
      const { data } = await api.post("/orders", {
        artwork_id: art.id, origin_url: window.location.origin, use_rebate: useRebate, insurance,
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.success("Order placed — fully covered by your trade credit.");
        navigate("/buyer");
      }
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Could not start checkout");
      setBuying(false);
    }
  };

  if (!art) return (<div><Navbar /><div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-[var(--gold)]" /></div></div>);

  const available = art.status === "available";

  return (
    <div>
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/gallery" className="inline-flex items-center gap-2 text-sm text-[var(--text-muted)] hover:text-[var(--gold)] mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to gallery
        </Link>

        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="sticky top-28 rounded-2xl overflow-hidden bg-[var(--bg-secondary)] border border-[var(--border-subtle)]">
              <img src={art.image_url} alt={art.title} className="w-full object-contain max-h-[75vh]" data-testid="artwork-detail-image" />
            </div>
          </div>

          <div className="lg:col-span-5">
            <span className="overline-badge">{art.category}</span>
            <h1 className="font-serif text-4xl sm:text-5xl mt-3 leading-tight" data-testid="artwork-detail-title">{art.title}</h1>
            <Link to={`/artist/${art.artist_id}`} className="text-[var(--text-secondary)] mt-2 inline-block hover:text-[var(--gold)]">
              by {art.artist_name}
            </Link>

            <div className="escrow-badge mt-5"><ShieldCheck className="w-3.5 h-3.5" /> Artist paid on delivery</div>

            <p className="text-[var(--text-secondary)] leading-relaxed mt-6">{art.description}</p>

            <div className="grid grid-cols-2 gap-4 mt-8 text-sm">
              {[[Palette, "Medium", art.medium], [Ruler, "Dimensions", art.dimensions],
                [MapPin, "Origin", art.origin || "—"], [ScrollText, "Year", art.year || "—"]].map(([Icon, l, v]) => (
                <div key={l} className="flex gap-3">
                  <Icon className="w-4 h-4 text-[var(--gold)] mt-0.5 shrink-0" />
                  <div><div className="text-[var(--text-muted)] text-xs">{l}</div><div className="font-medium">{v}</div></div>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-2 mt-6 text-sm text-[var(--text-secondary)]">
              <BadgeCheck className="w-4 h-4 text-[var(--escrow-green)]" /> Certificate of Authenticity · <span className="font-mono">{art.certificate}</span>
            </div>

            <div className="mt-8 p-6 rounded-2xl border border-[var(--border-subtle)] bg-white">
              <div className="flex items-end justify-between">
                <div>
                  <div className="text-xs text-[var(--text-muted)]">Price</div>
                  <div className="font-serif text-4xl" data-testid="artwork-detail-price">{money(art.price)}</div>
                </div>
                {available ? <span className="escrow-badge">Available</span>
                  : <span className="text-xs font-mono px-3 py-1 rounded-full bg-stone-900 text-white">{art.status === "sold" ? "Sold" : "Reserved"}</span>}
              </div>

              {isDesigner && available && (
                <label className="flex items-center gap-3 mt-5 p-3 rounded-lg bg-[var(--gold-light)] cursor-pointer" data-testid="rebate-toggle">
                  <input type="checkbox" checked={useRebate} onChange={(e) => setUseRebate(e.target.checked)} className="accent-[var(--gold)] w-4 h-4" />
                  <div className="text-sm">
                    <span className="font-medium flex items-center gap-1"><Percent className="w-3.5 h-3.5" /> Apply trade rebate</span>
                    <span className="text-[var(--text-muted)] text-xs">
                      {discountPct > 0 && `${discountPct}% discount`}{discountPct > 0 && credit > 0 && " + "}
                      {credit > 0 && `${money(credit)} credit`}
                    </span>
                  </div>
                </label>
              )}

              {available && (
                <label className="flex items-center gap-3 mt-3 p-3 rounded-lg border border-[var(--border-subtle)] cursor-pointer" data-testid="insurance-toggle">
                  <input type="checkbox" checked={insurance} onChange={(e) => setInsurance(e.target.checked)} className="accent-[var(--gold)] w-4 h-4" />
                  <div className="text-sm">
                    <span className="font-medium flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Add shipment insurance</span>
                    <span className="text-[var(--text-muted)] text-xs">Covers transit damage or loss (+{money(art.price * 0.02)})</span>
                  </div>
                </label>
              )}

              {((useRebate && isDesigner) || insurance) && (
                <div className="mt-4 text-sm flex justify-between border-t border-[var(--border-subtle)] pt-4">
                  <span className="text-[var(--text-muted)]">You pay</span>
                  <span className="font-serif text-2xl" data-testid="estimated-total">{money(estimated)}</span>
                </div>
              )}

              <button onClick={buy} disabled={!available || buying} data-testid="buy-escrow-btn"
                className="btn-gold w-full mt-5 rounded-full py-4 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
                {buying && <Loader2 className="w-4 h-4 animate-spin" />}
                {available ? "Buy now" : "Unavailable"}
              </button>
              <p className="text-xs text-[var(--text-muted)] text-center mt-3">
                You're charged at purchase. The artist is paid once your artwork is delivered.
              </p>
            </div>

            {art.provenance && (
              <div className="mt-6 text-sm">
                <div className="overline-badge">Provenance</div>
                <p className="text-[var(--text-secondary)] mt-2 leading-relaxed">{art.provenance}</p>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
